import React, { FunctionComponent, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Card } from "react-bootstrap"
import { API } from "../../api/api"
import { CenterLayout } from "../../presentationals/layouts/center"
import { LoadingOverlay } from "../../components/loading-overlay/loading-overlay"
import { Trans, withTranslation, WithTranslation } from "react-i18next"
import check2circle from "url:bootstrap-icons/icons/check2-circle.svg"
import xoctagon from "url:bootstrap-icons/icons/x-octagon.svg"
import Icon from "../../components/icon/icon"

import styles from "./email-activation.module.scss"

interface EmailActivationState {
    success: boolean | null
    result: string | null
    isLoading: boolean
}

const EmailActivation: FunctionComponent<WithTranslation> = ({
    t,
}: WithTranslation) => {
    const [searchParams] = useSearchParams()
    const userId = searchParams.get("userId")
    const token = searchParams.get("token")
    const [emailActivationState, setEmailActivationState] =
        useState<EmailActivationState>({
            success: null,
            result: null,
            isLoading: false,
        })

    useEffect(() => {
        if (userId && token) {
            const activateUser = async () => {
                setEmailActivationState({
                    success: null,
                    result: null,
                    isLoading: true,
                })
                const res = await API.USER.activateUser(userId, token)
                if (res === null) {
                    setEmailActivationState({
                        success: true,
                        result: t("email_activation.success_text"),
                        isLoading: false,
                    })
                } else {
                    setEmailActivationState({
                        success: false,
                        result: res.error
                            ? t("generic.error_text", {
                                  errorMessage: res.error,
                              })
                            : t("generic.unknown_error_text"),
                        isLoading: false,
                    })
                }
            }
            activateUser().catch((err) => {
                setEmailActivationState({
                    success: false,
                    result: err.message
                        ? t("generic.error_text", {
                              errorMessage: err.message,
                          })
                        : t("generic.unknown_error_text"),
                    isLoading: false,
                })
            })
        } else {
            setEmailActivationState({
                success: false,
                result: t("generic.unknown_error_text"),
                isLoading: false,
            })
        }
    }, [])
    return (
        <CenterLayout>
            <Card className={styles.card}>
                <Card.Body className={styles.cardBody}>
                    <LoadingOverlay isLoading={emailActivationState.isLoading}>
                        <Trans>generic.fetching</Trans>
                    </LoadingOverlay>
                    <img
                        className={styles.logo}
                        src={String(
                            new URL("../../assets/logo.svg", import.meta.url)
                        )}
                        alt="Anio Logo"
                    />
                    <div className={styles.heading}>
                        <h1>
                            <Trans>email_activation.title</Trans>
                        </h1>
                        <hr></hr>
                    </div>
                    {emailActivationState.success !== null && (
                        <>
                            <h2>
                                <Trans>
                                    {emailActivationState.success
                                        ? "email_activation.success_title"
                                        : "generic.error_title"}
                                </Trans>
                            </h2>
                            <Icon
                                size="48px"
                                color={
                                    emailActivationState.success
                                        ? "green"
                                        : "red"
                                }
                            >
                                {emailActivationState.success
                                    ? check2circle
                                    : xoctagon}
                            </Icon>
                            {emailActivationState.result && (
                                <p>{emailActivationState.result}</p>
                            )}
                            {!emailActivationState.success && (
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://anio.zendesk.com/hc/de"
                                >
                                    <Trans>generic.support</Trans>
                                </a>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </CenterLayout>
    )
}

export default withTranslation()(EmailActivation)
